import { useEffect, useMemo } from 'react'

import { addUploaderTracking } from './addUploaderTracking'
import { createUppy } from './createUppy'
import { useS3PreSignedURL } from './getS3PresignUrl'
import { Uploader } from './Uploader'
import type { UploaderEvents } from './Uploader'
import { defaultRestrictions } from './values/UploaderRestrictions'
import type { UploaderRestrictions } from './values/UploaderRestrictions'
import type { UploadTransport } from './values/UploadTransport'

/**
 * Returns an uploader instance for the given id with
 * uploader events bound to the redux store.
 * It also returns the current state of the uploader
 * and some callbacks.
 */
export const useUploader = ({
  id,
  fileRestrictions = defaultRestrictions,
  eventHandlers,
  getCustomCount,
  userId,
  organizationId,
  transport,
  onUploadInitiated,
  prepareFile,
}: {
  id: string
  fileRestrictions?: UploaderRestrictions
  eventHandlers?: Partial<UploaderEvents>
  getCustomCount?: () => number
  userId: string
  organizationId: string
  transport?: UploadTransport
  onUploadInitiated?: () => void
  prepareFile?: (file: File, signal: AbortSignal) => Promise<File>
}): Uploader => {
  const getS3PresignUrl = useS3PreSignedURL()

  const uploader = useMemo(() => {
    const uppyInstance = createUppy({
      id,
      organizationId,
      userId,
      getS3PresignUrl,
      transport,
    })

    const uploaderInstance = new Uploader(
      id,
      organizationId,
      uppyInstance,
      fileRestrictions,
      getCustomCount,
      onUploadInitiated,
      prepareFile,
    )

    if (eventHandlers) {
      Object.keys(eventHandlers).forEach((eventName) => {
        const handler = eventHandlers[eventName as keyof UploaderEvents]
        if (handler) {
          uploaderInstance.on(eventName as keyof UploaderEvents, handler)
        }
      })
    }

    addUploaderTracking(uploaderInstance)

    return uploaderInstance
  }, [
    id,
    userId,
    organizationId,
    fileRestrictions,
    getS3PresignUrl,
    getCustomCount,
    eventHandlers,
    transport,
    onUploadInitiated,
    prepareFile,
  ])

  useEffect(() => {
    return (): void => {
      uploader.close()
    }
  }, [uploader])

  return uploader
}
